import React, { useState, useMemo, memo, type FC, type Key } from 'react'
import { Button, type TableColumnType } from 'antd'
import { AddCircleRegular, Copy16Regular } from '@fluentui/react-icons'

import {
  type IUserPermission,
  type ICreateEditPanelState
} from '../../../constants/types/common'
import { type IOktaUser } from '../../../constants/types/generatedTypesAliases'
import { parsePermissionRowKey, setPermissionRowKey } from '../../../constants/helpers/common'
import { InfoTag } from '../../shared'

import {
  RemovePermissionsPopover,
  PermissionTableActions,
  PermissionTable,
  CreateOrEditUserPermissionsPanel,
  CopyPermissionsPanel
} from '../'

interface IUserDetailsPermissionsProps {
  selectedUser: IOktaUser
  permissions: IUserPermission[]
  loading: boolean
}

export const UserDetailsPermissions: FC<IUserDetailsPermissionsProps> = memo(({ selectedUser, permissions, loading }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
  const [copyPermissionsDrawerVisible, setCopyPermissionsDrawerVisible] = useState<boolean>(false)
  const [createEditPanelState, setCreateEditPanelState] = useState<ICreateEditPanelState>(null)

  const selectedRelationships = useMemo(() => (
    selectedRowKeys.map(rowKey => parsePermissionRowKey(rowKey as string))
  ), [selectedRowKeys])

  const handleOpenCopyPermissionDrawer = (permission) => {
    setSelectedRowKeys([setPermissionRowKey(permission)])
    setCopyPermissionsDrawerVisible(true)
  }

  const handleEditPermissionDrawer = (permission: IUserPermission) => {
    setCreateEditPanelState({ operation: 'edit', payload: permission })
  }

  const rowActionButtons: TableColumnType<IUserPermission> = {
    title: '',
    render: (_, record) => (
      <PermissionTableActions
        selectedUserId={selectedUser.id}
        permission={record}
        copyPermission={handleOpenCopyPermissionDrawer}
        editPermission={handleEditPermissionDrawer}
      />
    )
  }

  const bulkActionRow = (
    <>
      {
        selectedRowKeys.length
          ? (
              <span className='bulck-action-row__spacer'>
                <InfoTag
                  text={`${selectedRowKeys.length} selected`}
                  onClose={() => setSelectedRowKeys([])}
                />
                <RemovePermissionsPopover
                  selectedUserId={selectedUser.id ?? null}
                  selectedRelationships={selectedRelationships}
                  removePermissionsCallback={() => setSelectedRowKeys([])} />
                <Button
                  color='primary'
                  variant='text'
                  className='btn-space'
                  icon={<Copy16Regular />}
                  onClick={() => setCopyPermissionsDrawerVisible(true)}>
                  Copy Permissions
                </Button>
              </span>
            )
          : (
              <Button
                color='primary'
                variant='outlined'
                className='btn-space__extra'
                icon={<AddCircleRegular />}
                onClick={() => setCreateEditPanelState({ operation: 'create' })}>
                Add a Permission
              </Button>
            )
      }
    </>
  )

  return (
    <>
      <PermissionTable
        permissions={permissions}
        loading={loading}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        rowActionButtons={rowActionButtons}
        bulkActionRow={bulkActionRow}
      />

      <CreateOrEditUserPermissionsPanel
        selectedUsers={[selectedUser]}
        createEditPanelState={createEditPanelState}
        setCreateEditPanelState={setCreateEditPanelState}
      />

      <CopyPermissionsPanel
        permissions={selectedRelationships}
        dropSelectedPermissions={() => setSelectedRowKeys([])}
        setCopyPermissionsDrawerVisible={setCopyPermissionsDrawerVisible}
        copyPermissionsDrawerVisible={copyPermissionsDrawerVisible}
      />
    </>
  )
})
